import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedSection extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #load() {
    const sectionId = this.dataset.sectionId;
    const currentProductId = this.dataset.currentProductId;
    const maxProducts = Number.parseInt(this.dataset.maxProducts || '4', 10);
    const productIds = RecentlyViewed.getProducts()
      .filter((id) => id !== currentProductId)
      .slice(0, Number.isNaN(maxProducts) ? 4 : maxProducts);

    if (!sectionId || productIds.length === 0) {
      this.hidden = true;
      return;
    }

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, abortController.signal);
      if (abortController.signal.aborted) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('[data-recently-viewed-results]');
      const current = this.querySelector('[data-recently-viewed-results]');
      if (!incoming || !current || incoming.children.length === 0) {
        this.hidden = true;
        return;
      }

      const order = new Map(productIds.map((id, index) => [id, index]));
      const cards = Array.from(incoming.children);
      cards.sort((a, b) => {
        const first = order.get(a.getAttribute('data-product-id') || '') ?? Number.MAX_SAFE_INTEGER;
        const second = order.get(b.getAttribute('data-product-id') || '') ?? Number.MAX_SAFE_INTEGER;
        return first - second;
      });
      incoming.replaceChildren(...cards);
      current.replaceWith(incoming);
      this.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('Recently viewed products could not be loaded.', error);
      this.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
